import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ExternalLink, Loader2 } from 'lucide-react';

import {
  useUserFeedQuery,
  type UserFeedItem,
} from '@/client/api/admin/user-content.query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const PAGE_SIZE = 25;

const itemText = (i: UserFeedItem): string =>
  (i.title || i.body || i.message || i.type || 'Notification') as string;
const itemEid = (i: UserFeedItem): number | null =>
  (i.entity_id ?? i.eid ?? null) as number | null;
const itemDate = (i: UserFeedItem): string | null =>
  (i.date_added ?? i.created_at ?? null) as string | null;
const isUnread = (i: UserFeedItem): boolean =>
  i.read === false || i.read === 0 || i.read == null;

/**
 * The user's in-app notification feed.
 *
 * ⚠️ Backed by `GET /api/admin/users/<uid>/feed`, which DOES NOT EXIST on
 * ff-api yet (see docs/BACKEND_ENDPOINTS_user_content.md). Until it lands this
 * tab shows the error/empty state below.
 */
const NotificationFeedTab = ({ uid }: { uid: number }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useUserFeedQuery(uid, {
    page,
    page_size: PAGE_SIZE,
  });

  const items: UserFeedItem[] = data?.feed ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
        <Bell className="mx-auto mb-2 h-6 w-6 text-amber-500" />
        Couldn’t load notifications. This tab reads{' '}
        <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">GET /api/admin/user/info/{uid}/notifications</code>.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
        This user has no notifications.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {data?.count ?? items.length} notification
          {(data?.count ?? items.length) === 1 ? '' : 's'}
        </p>
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {items.map((item, idx) => {
          const eid = itemEid(item);
          const date = itemDate(item);
          return (
            <li
              key={(item.id as string | number) ?? idx}
              className={`flex items-start gap-3 px-4 py-3 ${
                isUnread(item) ? 'bg-indigo-50/40' : ''
              }`}
            >
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                <Bell className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900">{itemText(item)}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                  {item.type && <span className="capitalize">{item.type}</span>}
                  {date && <span>· {new Date(date).toLocaleString()}</span>}
                  {eid != null && (
                    <Link
                      to={`/admin/listings/${eid}`}
                      className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                    >
                      Listing #{eid}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
              {isUnread(item) && (
                <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-500" />
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || isFetching}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={items.length < PAGE_SIZE || isFetching}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default NotificationFeedTab;
